import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  PaperlessPreferenceFieldsetComponent,
  ProtectionStartSummaryPanelComponent,
  TermsAndConditionsFieldsetComponent,
} from '@checkout/ui-components';
import {
  CheckoutFeature,
  WorldpayRedirectResponse,
} from '@checkout/util-models';
import { HasSelectedProductTypeDirective } from '@domgen/dgx-fe-basket';
import { ProductType, mapToLatestFrom } from '@domgen/dgx-fe-common';
import { fadeOut } from '@domgen/dgx-fe-components/animation';
import {
  AddressToArrayPipe,
  TestHookDirective,
} from '@domgen/dgx-fe-components/general';
import { LoadingOverlayComponent } from '@domgen/dgx-fe-components/loading';
import { FeatureEnabledDirective } from '@domgen/dgx-fe-config';
import { AdditionalAddressComponent } from '@domgen/dgx-fe-forms/address';
import {
  FormFieldsetComponent,
  FormFieldsetDirective,
  FormRowComponent,
  FormSubmitButtonComponent,
} from '@domgen/dgx-fe-forms/layout';
import {
  FormDisabledDirective,
  FormSubmitDirective,
} from '@domgen/dgx-fe-forms/shared';
import { GAClickDirective, GAFormSubmitDirective } from '@domgen/dgx-fe-ga';
import { TranslatePipe } from '@domgen/dgx-fe-i18n';
import { QMSanitiseDirective } from '@domgen/dgx-fe-qm';
import { WorldpayTestHooks } from '../../models/test/worldpay-test-hooks.enum';
import { WorldpayFormComponentService } from '../../services/worldpay-form-component/worldpay-form-component.service';

@Component({
  selector: 'checkout-worldpay-form',
  templateUrl: './worldpay-form.component.html',
  styleUrls: ['./worldpay-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeOut],
  providers: [WorldpayFormComponentService],
  standalone: true,
  imports: [
    AdditionalAddressComponent,
    AddressToArrayPipe,
    AsyncPipe,
    FeatureEnabledDirective,
    FormDisabledDirective,
    FormFieldsetComponent,
    FormFieldsetDirective,
    FormRowComponent,
    FormSubmitButtonComponent,
    FormSubmitDirective,
    GAClickDirective,
    GAFormSubmitDirective,
    HasSelectedProductTypeDirective,
    LoadingOverlayComponent,
    NgClass,
    NgFor,
    NgIf,
    PaperlessPreferenceFieldsetComponent,
    ProtectionStartSummaryPanelComponent,
    QMSanitiseDirective,
    ReactiveFormsModule,
    TermsAndConditionsFieldsetComponent,
    TestHookDirective,
    TranslatePipe,
  ],
})
export class WorldpayFormComponent implements OnInit, OnDestroy {
  @Output() submitted = new EventEmitter<string>();

  productType = ProductType;
  termsAndConditionsFeature = CheckoutFeature.TermsAndConditions;
  testHooks = WorldpayTestHooks;

  constructor(
    readonly cs: WorldpayFormComponentService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.cs.buildForm();

    this.cs.resultSuccess$
      .pipe(
        mapToLatestFrom(this.cs.redirectResponse$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((redirectResponse: WorldpayRedirectResponse) => {
        this.submitted.emit(redirectResponse.orderNumber);
      });
  }

  ngOnDestroy(): void {
    this.cs.destroy();
  }
}
